import React, { useState, useEffect } from 'react';
import { FileText, Eye, Mail, Calendar, CheckCircle, Clock, XCircle, Trash2, X, Plus, Edit, Copy, Download, Send, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { quoteService, Quote } from '../../services/quoteService';
import { QuoteWithDetails } from '../../types/product';
import { notificationService } from '../../services/notificationService';
import ConfirmDialog from '../ui/ConfirmDialog';
import QuoteForm from './QuoteForm';
import { pdfService } from '../../services/pdfService';
import { zeptoMailService } from '../../services/zeptoMailService';

interface QuoteListProps {
  onCreateQuote?: () => void;
}

const QuoteList = ({ onCreateQuote }: QuoteListProps) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [selectedQuoteWithDetails, setSelectedQuoteWithDetails] = useState<QuoteWithDetails | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [generatingPDF, setGeneratingPDF] = useState<number | null>(null);
  const [sendingEmail, setSendingEmail] = useState<number | null>(null);
  const [approvingQuote, setApprovingQuote] = useState<number | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'Todas', color: 'bg-gray-100 text-gray-800' },
    { value: 'pending', label: 'Pendientes', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'contacted', label: 'Contactadas', color: 'bg-blue-100 text-blue-800' },
    { value: 'quoted', label: 'Cotizadas', color: 'bg-green-100 text-green-800' },
    { value: 'sent', label: 'Enviadas', color: 'bg-purple-100 text-purple-800' },
    { value: 'approved', label: 'Aprobadas', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'closed', label: 'Cerradas', color: 'bg-gray-100 text-gray-800' }
  ];

  const statusIcons = {
    pending: <Clock className="w-4 h-4" />,
    contacted: <Mail className="w-4 h-4" />,
    quoted: <CheckCircle className="w-4 h-4" />,
    sent: <Mail className="w-4 h-4" />,
    approved: <CheckCircle className="w-4 h-4" />,
    closed: <XCircle className="w-4 h-4" />
  };

  useEffect(() => {
    loadQuotes();
  }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadQuotes = async (skipAutoUpdate = false) => {
    setIsLoading(true);
    try {
      // Solo actualizar cotizaciones con productos a estado "Cotizadas" si no se especifica lo contrario
      if (!skipAutoUpdate) {
        await quoteService.updateQuotesWithProductsToQuoted();
      }
      
      let data: Quote[] = [];
      if (filterStatus === 'all') {
        data = await quoteService.getQuotes();
      } else {
        data = await quoteService.getQuotesByStatus(filterStatus as Quote['status']);
      }
      
      // Para cada cotización, cargar la información del cliente si tiene customer_id
      const quotesWithCustomers = await Promise.all(
        data.map(async (quote) => {
          if (quote.customer_id) {
            try {
              const quoteWithDetails = await quoteService.getQuoteWithCustomer(quote.id!);
              if (quoteWithDetails?.customers) {
                return {
                  ...quote,
                  customerName: quoteWithDetails.customers.name
                };
              }
            } catch (error) {
              console.error('Error al cargar cliente para cotización:', quote.id, error);
            }
          }
          return quote;
        })
      );
      
      setQuotes(quotesWithCustomers);
    } catch (error) {
      console.error('Error al cargar cotizaciones:', error);
    } finally {
      setIsLoading(false);
    }
  };


  // Función para cargar los detalles de una cotización
  const loadQuoteDetails = async (quoteId: number) => {
    setLoadingDetails(true);
    try {
      const quoteWithDetails = await quoteService.getQuoteWithCustomer(quoteId);
      setSelectedQuoteWithDetails(quoteWithDetails);
    } catch (error) {
      console.error('Error al cargar detalles de cotización:', error);
      notificationService.error('Error', 'No se pudieron cargar los detalles de la cotización.');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Función para calcular el total de una cotización
  const calculateQuoteTotal = (quoteDetails: QuoteWithDetails) => {
    if (!quoteDetails.quote_details || quoteDetails.quote_details.length === 0) {
      return 0;
    }
    return quoteDetails.quote_details.reduce((total, detail) => {
      return total + (detail.quantity * detail.unit_price);
    }, 0);
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<number | null>(null);

  const handleDelete = async (quoteId: number) => {
    setQuoteToDelete(quoteId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (quoteToDelete !== null) {
      try {
        const success = await quoteService.deleteQuote(quoteToDelete);
        if (success) {
          loadQuotes();
          if (selectedQuote?.id === quoteToDelete) {
            setSelectedQuote(null);
          }
          notificationService.success('Cotización eliminada', 'La cotización ha sido eliminada exitosamente.');
        }
      } catch (error) {
        console.error('Error al eliminar cotización:', error);
        notificationService.error('Error al eliminar cotización', 'No se pudo eliminar la cotización. Por favor, intente nuevamente.');
      } finally {
        setShowDeleteConfirm(false);
        setQuoteToDelete(null);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setQuoteToDelete(null);
  };

  const handleEdit = (quote: Quote) => {
    setEditingQuote(quote);
  };

  const handleDuplicate = async (quote: Quote) => {
    try {
      const duplicatedQuote = await quoteService.duplicateQuote(quote.id!);
      if (duplicatedQuote) {
        loadQuotes();
        notificationService.success('Cotización duplicada', 'La cotización ha sido duplicada exitosamente con un nuevo número.');
      } else {
        notificationService.error('Error al duplicar cotización', 'No se pudo duplicar la cotización. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('Error al duplicar cotización:', error);
      notificationService.error('Error al duplicar cotización', 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    }
  };

  const handleEditSave = () => {
    setEditingQuote(null);
    loadQuotes();
  };

  const handleEditCancel = () => {
    setEditingQuote(null);
  };

  const handleGeneratePDF = async (quote: Quote) => {
    try {
      // Establecer el ID de la cotización que se está procesando
      setGeneratingPDF(quote.id!);
      
      // Obtener la cotización completa con detalles
      const quoteWithDetails = await quoteService.getQuoteWithDetails(quote.id!);
      if (quoteWithDetails) {
        await pdfService.generateQuotePDF(quoteWithDetails);
        notificationService.success('PDF generado', 'El PDF de la cotización ha sido generado exitosamente.');
      } else {
        notificationService.error('Error', 'No se pudo obtener los detalles de la cotización para generar el PDF.');
      }
    } catch (error) {
      console.error('Error al generar PDF:', error);
      notificationService.error('Error al generar PDF', 'No se pudo generar el PDF. Por favor, intente nuevamente.');
    } finally {
      // Limpiar el estado de carga
      setGeneratingPDF(null);
    }
  };

  // Función para enviar cotización por correo
  const handleSendEmail = async (quote: Quote) => {
    try {
      // Establecer el ID de la cotización que se está procesando
      setSendingEmail(quote.id!);
      
      console.log('🚀 Iniciando envío de correo para cotización:', quote.id);
      
      // Obtener la cotización completa con detalles y cliente
      const quoteWithDetails = await quoteService.getQuoteWithCustomer(quote.id!);
      if (quoteWithDetails) {
        console.log('📋 Detalles de cotización obtenidos:', quoteWithDetails);
        
        // Enviar el correo
        const emailResult = await zeptoMailService.sendQuoteEmail(quoteWithDetails);
        
        console.log('📧 Resultado del envío de correo:', emailResult);
        
        if (emailResult.success) {
          console.log('✅ Correo enviado exitosamente, actualizando estado a "sent"');
          
          // Actualizar el estado de la cotización a "Enviada"
          const updatedQuote = await quoteService.updateQuoteStatus(quote.id!, 'sent');
          
          if (updatedQuote) {
            console.log('✅ Estado actualizado correctamente:', updatedQuote);
            
            // Actualizar el estado localmente para una respuesta inmediata
            setQuotes(prevQuotes =>
              prevQuotes.map(q =>
                q.id === quote.id ? { ...q, status: 'sent' as Quote['status'] } : q
              )
            );
            
            // Recargar las cotizaciones para asegurar sincronización (omitir actualización automática)
            await loadQuotes(true);
            
            // Actualizar la cotización seleccionada si es la misma
            if (selectedQuote?.id === quote.id) {
              // Obtener la cotización actualizada desde la lista recargada
              const updatedQuote = quotes.find(q => q.id === quote.id);
              if (updatedQuote) {
                setSelectedQuote(updatedQuote);
              }
            }
            
            notificationService.success('Correo enviado', 'La cotización ha sido enviada exitosamente y su estado ha sido actualizado a "Enviada".');
          } else {
            console.error('❌ Error al actualizar el estado de la cotización');
            notificationService.error('Error', 'El correo se envió pero no se pudo actualizar el estado. Por favor, actualice manualmente.');
          }
        } else {
          console.error('❌ Error al enviar correo:', emailResult.message);
          notificationService.error('Error al enviar correo', emailResult.message);
        }
      } else {
        console.error('❌ No se pudieron obtener los detalles de la cotización');
        notificationService.error('Error', 'No se pudo obtener los detalles de la cotización para enviar el correo.');
      }
    } catch (error) {
      console.error('❌ Error al enviar correo:', error);
      notificationService.error('Error al enviar correo', 'No se pudo enviar el correo. Por favor, intente nuevamente.');
    } finally {
      // Limpiar el estado de carga
      setSendingEmail(null);
    }
  };

  // Función para aprobar una cotización
  const handleApprove = async (quote: Quote) => {
    try {
      // Establecer el ID de la cotización que se está procesando
      setApprovingQuote(quote.id!);
      
      console.log('🚀 Iniciando aprobación de cotización:', quote.id);
      
      // Actualizar el estado de la cotización a "Aprobada"
      const updatedQuote = await quoteService.updateQuoteStatus(quote.id!, 'approved');
      
      if (updatedQuote) {
        console.log('✅ Cotización aprobada correctamente:', updatedQuote);
        
        // Actualizar el estado localmente para una respuesta inmediata
        setQuotes(prevQuotes =>
          prevQuotes.map(q =>
            q.id === quote.id ? { ...q, status: 'approved' as Quote['status'] } : q
          )
        );
        
        // Recargar las cotizaciones para asegurar sincronización
        await loadQuotes(true);
        
        // Actualizar la cotización seleccionada si es la misma
        if (selectedQuote?.id === quote.id) {
          setSelectedQuote(updatedQuote);
        }
        
        notificationService.success('Cotización aprobada', 'La cotización ha sido aprobada exitosamente.');
      } else {
        console.error('❌ Error al aprobar la cotización');
        notificationService.error('Error', 'No se pudo aprobar la cotización. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('❌ Error al aprobar cotización:', error);
      notificationService.error('Error', 'No se pudo aprobar la cotización. Por favor, intente nuevamente.');
    } finally {
      // Limpiar el estado de carga
      setApprovingQuote(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  };

  // Función para extraer el código de cotización del mensaje
  const getQuoteCode = (quote: Quote) => {
    // Si el nombre ya tiene el formato "Cotización Pendiente - [Empresa]", extraer el código del mensaje
    if (quote.name && quote.name.startsWith('Cotización Pendiente - ')) {
      // Para cotizaciones pendientes, extraer el código del mensaje
      const messageParts = quote.message.split(' - ');
      let quoteCode = messageParts[0] || 'Sin código';
      
      // Si el código contiene la palabra "Cotización", eliminarla
      if (quoteCode.startsWith('Cotización ')) {
        quoteCode = quoteCode.replace('Cotización ', '');
      }
      
      return quoteCode || 'Pendiente';
    }
    
    // Para cotizaciones normales, extraer el código del mensaje
    const messageParts = quote.message.split(' - ');
    let quoteCode = messageParts[0] || 'Sin código';
    
    // Si el código contiene la palabra "Cotización", eliminarla
    if (quoteCode.startsWith('Cotización ')) {
      quoteCode = quoteCode.replace('Cotización ', '');
    }
    
    return quoteCode;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Cotizaciones</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Total: {quotes.length} cotizaciones</span>
          <Button
            onClick={onCreateQuote || (() => {
              // Si no se proporciona función, usar redirección como fallback
              window.location.href = '/admin?view=create-quote';
            })}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Cotización
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((status) => (
            <button
              key={status.value}
              onClick={() => setFilterStatus(status.value)}
              className={`px-3 py-2 sm:px-4 rounded-lg font-medium transition-colors text-sm ${
                filterStatus === status.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Vista móvil: Modal para detalles */}
      {selectedQuote && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Detalles de Cotización</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedQuote(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {loadingDetails ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Información del cliente - Solo empresa */}
                  {selectedQuote.company && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Empresa</label>
                      <p className="text-gray-900">{selectedQuote.company}</p>
                    </div>
                  )}

                  {/* Productos y cantidades */}
                  {selectedQuoteWithDetails?.quote_details && selectedQuoteWithDetails.quote_details.length > 0 ? (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Productos</label>
                      <div className="mt-2 space-y-2">
                        {selectedQuoteWithDetails.quote_details.map((detail, index) => (
                          <div key={detail.id || index} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  {(detail as any).products?.name || `Producto #${detail.product_id}`}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Cantidad: {detail.quantity} × ${detail.unit_price.toFixed(2)}
                                </p>
                                {detail.notes && (
                                  <p className="text-sm text-gray-500 mt-1">Notas: {detail.notes}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-gray-900">
                                  ${(detail.quantity * detail.unit_price).toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : selectedQuote.product_name ? (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Producto</label>
                      <p className="text-gray-900">{selectedQuote.product_name}</p>
                    </div>
                  ) : null}

                  {/* Precio total */}
                  {selectedQuoteWithDetails?.quote_details && selectedQuoteWithDetails.quote_details.length > 0 && (
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex justify-between items-center">
                        <label className="text-sm font-medium text-gray-700">Precio Total</label>
                        <p className="text-lg font-bold text-primary-600">
                          ${calculateQuoteTotal(selectedQuoteWithDetails).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Términos de pago y entrega */}
                  {selectedQuoteWithDetails?.payment_terms && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Término de Pago</label>
                      <p className="text-gray-900">{selectedQuoteWithDetails.payment_terms}</p>
                    </div>
                  )}

                  {selectedQuoteWithDetails?.delivery_terms && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Término de Entrega</label>
                      <p className="text-gray-900">{selectedQuoteWithDetails.delivery_terms}</p>
                    </div>
                  )}

                  {selectedQuote.created_at && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Fecha de creación</label>
                      <p className="text-gray-900">{formatDate(selectedQuote.created_at)}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Quotes List */}
        <div className="lg:col-span-2 space-y-4">
          {quotes.length === 0 ? (
            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-sm border text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No se encontraron cotizaciones</p>
            </div>
          ) : (
            quotes.map((quote) => (
              <div
                key={quote.id}
                className={`bg-white p-4 sm:p-6 rounded-lg shadow-sm border cursor-pointer transition-all hover:shadow-md ${
                  selectedQuote?.id === quote.id ? 'border-primary-500 ring-2 ring-primary-200' : ''
                }`}
                onClick={() => {
                  setSelectedQuote(quote);
                  loadQuoteDetails(quote.id!);
                }}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 truncate">{getQuoteCode(quote)}</h3>
                    <p className="text-sm text-gray-600 truncate">
                      {quote.name && quote.name.startsWith('Cotización Pendiente - ')
                        ? (quote.customerName || quote.name.replace('Cotización Pendiente - ', ''))
                        : quote.name
                      }
                    </p>
                    <p className="text-sm text-gray-600 truncate">{quote.email}</p>
                    {quote.company && (
                      <p className="text-sm text-gray-600 truncate">{quote.company}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    <span className={`px-2 py-1 sm:px-3 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(quote.status || 'pending')}`}>
                      {statusIcons[quote.status as keyof typeof statusIcons] || statusIcons.pending}
                      <span className="hidden sm:inline">{getStatusLabel(quote.status || 'pending')}</span>
                    </span>
                  </div>
                </div>

                {quote.product_name && (
                  <div className="mb-3">
                    <span className="text-sm font-medium text-gray-700">Producto: </span>
                    <span className="text-sm text-gray-600 truncate">{quote.product_name}</span>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    <span className="hidden sm:inline">
                      {quote.created_at && formatDate(quote.created_at)}
                    </span>
                    <span className="sm:hidden">
                      {quote.created_at && new Date(quote.created_at).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(quote);
                      }}
                      className="text-blue-600 hover:text-blue-800 hover:border-blue-300"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(quote);
                      }}
                      className="text-green-600 hover:text-green-800 hover:border-green-300"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGeneratePDF(quote);
                      }}
                      className="text-purple-600 hover:text-purple-800 hover:border-purple-300"
                      disabled={generatingPDF === quote.id}
                    >
                      {generatingPDF === quote.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </Button>
                    {quote.status === 'quoted' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendEmail(quote);
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:border-blue-300"
                        disabled={sendingEmail === quote.id}
                      >
                        {sendingEmail === quote.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    {quote.status === 'sent' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(quote);
                        }}
                        className="text-green-600 hover:text-green-800 hover:border-green-300"
                        disabled={approvingQuote === quote.id}
                      >
                        {approvingQuote === quote.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(quote.id!);
                      }}
                      className="text-red-600 hover:text-red-800 hover:border-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Quote Details - Solo visible en desktop */}
        <div className="hidden lg:block lg:col-span-1">
          {selectedQuote ? (
            <div className="bg-white p-6 rounded-lg shadow-sm border sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalles de Cotización</h3>
              
              {loadingDetails ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Información del cliente - Solo empresa */}
                  {selectedQuote.company && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Empresa</label>
                      <p className="text-gray-900">{selectedQuote.company}</p>
                    </div>
                  )}

                  {/* Productos y cantidades */}
                  {selectedQuoteWithDetails?.quote_details && selectedQuoteWithDetails.quote_details.length > 0 ? (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Productos</label>
                      <div className="mt-2 space-y-2">
                        {selectedQuoteWithDetails.quote_details.map((detail, index) => (
                          <div key={detail.id || index} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  {(detail as any).products?.name || `Producto #${detail.product_id}`}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Cantidad: {detail.quantity} × ${detail.unit_price.toFixed(2)}
                                </p>
                                {detail.notes && (
                                  <p className="text-sm text-gray-500 mt-1">Notas: {detail.notes}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-gray-900">
                                  ${(detail.quantity * detail.unit_price).toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : selectedQuote.product_name ? (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Producto</label>
                      <p className="text-gray-900">{selectedQuote.product_name}</p>
                    </div>
                  ) : null}

                  {/* Precio total */}
                  {selectedQuoteWithDetails?.quote_details && selectedQuoteWithDetails.quote_details.length > 0 && (
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex justify-between items-center">
                        <label className="text-sm font-medium text-gray-700">Precio Total</label>
                        <p className="text-lg font-bold text-primary-600">
                          ${calculateQuoteTotal(selectedQuoteWithDetails).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Términos de pago y entrega */}
                  {selectedQuoteWithDetails?.payment_terms && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Término de Pago</label>
                      <p className="text-gray-900">{selectedQuoteWithDetails.payment_terms}</p>
                    </div>
                  )}

                  {selectedQuoteWithDetails?.delivery_terms && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Término de Entrega</label>
                      <p className="text-gray-900">{selectedQuoteWithDetails.delivery_terms}</p>
                    </div>
                  )}

                  {selectedQuote.created_at && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Fecha de creación</label>
                      <p className="text-gray-900">{formatDate(selectedQuote.created_at)}</p>
                    </div>
                  )}

                </div>
              )}
            </div>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-sm border text-center">
              <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Selecciona una cotización para ver los detalles</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de edición de cotización */}
      {editingQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            <QuoteForm
              quoteId={editingQuote.id}
              onSave={handleEditSave}
              onCancel={handleEditCancel}
            />
          </div>
        </div>
      )}

      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Confirmar Eliminación"
        message="¿Está seguro de que desea eliminar esta cotización? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default QuoteList;